import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Paper,
  Alert,
  AlertTitle,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import { Delete, ExpandMore, ExpandLess, Star } from '@mui/icons-material';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { CANCELLATION_CUTOFF_UI_DEFAULT, type WebsiteSettings } from '../types';
import SeasonalRenewalBanner from '../components/SeasonalRenewalBanner';
import CustomerPaymentInfo from '../components/CustomerPaymentInfo';
import PaymentMethodSetup from '../components/PaymentMethodSetup';
import CustomerAdditionalServicesEstimates from '../components/CustomerAdditionalServicesEstimates';
import CustomerAgreementsCard from '../components/agreements/CustomerAgreementsCard';
import { useProviderAgreementGate } from '../components/agreements/ProviderAgreementSigningDialog';

// Helper function to format UTC dates correctly (extract UTC components to avoid timezone shifts)
// When dates are stored as UTC midnight (e.g., 2026-01-12T00:00:00.000Z), they represent the calendar date
// We should extract UTC components directly, not convert to local time
const formatUTCDateString = (dateInput: Date | string): string => {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  // Extract UTC date components directly (these represent the calendar date)
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1; // getUTCMonth() returns 0-11, add 1 for display
  const day = date.getUTCDate();
  return `${month}/${day}/${year}`;
};

// Normalize service display names from backend (e.g. "Lawn Mowing & Trimming/Edging" -> "Lawn Mowing/Trimming")
const normalizeServiceDisplayName = (name: string): string => {
  if (!name) return name;
  const n = name.trim();
  const lawnMowingEdgingVariants = [
    'Lawn Mowing & Trimming/Edging', 'Lawn Mowing/Trim/Edge', 'Edging/Trimming',
    'Lawn Mowing/Edging', 'Trimming/Edging', 'Lawn Mowing & Trimming'
  ];
  if (lawnMowingEdgingVariants.includes(n)) return 'Lawn Mowing/Trimming';
  if (n.toLowerCase().includes('lawn mowing') && (n.includes('edging') || n.includes('Trim/Edge'))) {
    return 'Lawn Mowing/Trimming';
  }
  return n;
};

const CustomerDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [paymentSuccessToast, setPaymentSuccessToast] = useState(false);
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState<any[]>([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [declineDialog, setDeclineDialog] = useState({ open: false, service: null as any });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, service: null as any });
  const [deleting, setDeleting] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [paymentSetupDialog, setPaymentSetupDialog] = useState({ 
    open: false, 
    serviceId: '', 
    providerId: '', 
    services: [], 
    servicePricing: [], 
    totalPrice: 0 
  });
  const [paymentInfo, setPaymentInfo] = useState<any>(null);
  const [loadingPaymentInfo, setLoadingPaymentInfo] = useState(true);
  const [setupPaymentLoading, setSetupPaymentLoading] = useState(false);
  const [onboardingOffers, setOnboardingOffers] = useState<any[]>([]);
  const [loadingOnboarding, setLoadingOnboarding] = useState(true);
  const [onboardingDeclineDialog, setOnboardingDeclineDialog] = useState({ open: false, offer: null as any });
  const onboardingSectionRef = React.useRef<HTMLDivElement | null>(null);
  const focusOnboarding = searchParams.get('onboarding') === '1';
  const [seasonalContractConfirmDialog, setSeasonalContractConfirmDialog] = useState({ open: false, offer: null as any });
  const { requestSignatures, agreementDialog } = useProviderAgreementGate();
  const [providerContactDialog, setProviderContactDialog] = useState<{
    open: boolean;
    provider:
      | null
      | {
          name: string;
          businessName?: string;
          email?: string;
          phone?: string;
          businessLogo?: string;
        };
  }>({ open: false, provider: null });
  const [expandedServiceRows, setExpandedServiceRows] = useState<Set<string>>(new Set());
  const [cancellationCutoffText, setCancellationCutoffText] = useState(CANCELLATION_CUTOFF_UI_DEFAULT);

  // Show success message passed from service request submit (redirect from request-service page)
  useEffect(() => {
    const stateMessage = (location.state as any)?.successMessage;
    if (stateMessage && typeof stateMessage === 'string') {
      setSuccess(stateMessage);
      // Clear state so refresh doesn't re-show the message
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    fetchServices();
    fetchPaymentInfo();
    fetchOnboardingOffers();
    
    // Check for payment success parameter and show toast
    const paymentSuccess = searchParams.get('paymentSuccess');
    if (paymentSuccess === 'true') {
      setPaymentSuccessToast(true);
      // Remove the parameter from URL
      searchParams.delete('paymentSuccess');
      setSearchParams(searchParams, { replace: true });
      
      // Refresh payment info after a delay to allow webhook processing
      // The webhook may take a moment to process the payment method
      setTimeout(() => {
        console.log('[CUSTOMER_DASHBOARD] Refreshing payment info after payment success...');
        fetchPaymentInfo();
      }, 2000); // 2 second delay for webhook processing
      
      // Also refresh again after a longer delay as a fallback
      setTimeout(() => {
        console.log('[CUSTOMER_DASHBOARD] Second refresh of payment info after payment success...');
        fetchPaymentInfo();
      }, 5000); // 5 second delay as fallback
    }
    
    // Refresh data when the page regains focus (e.g., user navigates back from payment setup)
    const handleFocus = () => {
      fetchServices();
      fetchPaymentInfo();
      fetchOnboardingOffers();
    };

    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [location.pathname, searchParams, setSearchParams]);

  // After invite signup/login, scroll to provider offers to accept or decline
  useEffect(() => {
    if (!focusOnboarding || loadingOnboarding) return;
    const timer = window.setTimeout(() => {
      onboardingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [focusOnboarding, loadingOnboarding, onboardingOffers]);

  useEffect(() => {
    const loadCutoff = async () => {
      try {
        const res = await apiService.get<WebsiteSettings>('/website-settings');
        if (res?.data?.cancellationCutoffDescription) setCancellationCutoffText(res.data.cancellationCutoffDescription);
      } catch {
        // keep default
      }
    };
    loadCutoff();
  }, []);

  const fetchServices = async () => {
    try {
      setLoadingServices(true);
      
      // Try the /my-services endpoint first (designed for customers)
      let response;
      let myServicesFailed = false;
      try {
        console.log('[CUSTOMER_DASHBOARD] ==========================================');
        console.log('[CUSTOMER_DASHBOARD] Attempting to fetch services from /my-services endpoint...');
        console.log('[CUSTOMER_DASHBOARD] API call: GET /service-requests/my-services?limit=100');
        console.log('[CUSTOMER_DASHBOARD] Full URL will be:', window.location.origin + '/api/service-requests/my-services?limit=100');
        response = await apiService.get('/service-requests/my-services?limit=100');
        console.log('[CUSTOMER_DASHBOARD] /my-services call succeeded!');
        console.log('=== USING /my-services ENDPOINT (SUCCESS) ===');
        console.log('Full Response:', JSON.stringify(response, null, 2));
        console.log('Response success:', response.success);
        console.log('Response data:', response.data);
        
        if (response && response.success && response.data) {
          const services = (response.data as any).services || [];
          console.log('[CUSTOMER_DASHBOARD] Services from /my-services:', services.length);
          console.log('[CUSTOMER_DASHBOARD] Services array:', services);
          
          if (services.length === 0) {
            console.warn('[CUSTOMER_DASHBOARD] /my-services returned 0 services, checking fallback...');
          }
          console.log('Services:', services.map((s: any) => ({
            id: s._id || s.id,
            status: s.status,
            serviceType: s.serviceType?.name,
            provider: s.providerId?.businessName || s.providerId?.firstName,
            acceptedBidId: s.acceptedBidId?._id || s.acceptedBidId || 'none',
            hasPrice: s.price !== undefined && s.price !== null,
            price: s.price,
            isImportedCustomer: s.isImportedCustomer,
            serviceCategory: s.serviceType?.category
          })));
          
          // Log full service objects to inspect all fields
          console.log('[CUSTOMER_DASHBOARD] Full service objects:', services);
          
          // Deduplicate services: If multiple services exist with the same acceptedBidId, they are duplicates
          // Also deduplicate services for the same customer/provider/address/serviceType combination, preferring seasonal contracts
          // IMPORTANT: Include serviceType in the key to allow multiple services at the same address
          const serviceMap = new Map<string, any>();
          services.forEach((service: any) => {
            const customerId = service.customerId?._id?.toString() || service.customerId?.toString() || '';
            const providerId = service.providerId?._id?.toString() || service.providerId?.toString() || '';
            const acceptedBidId = service.acceptedBidId?._id?.toString() || service.acceptedBidId?.toString() || '';
            // Normalize address: lowercase, trim, remove extra spaces
            const address = (service.address?.street || '').toLowerCase().trim().replace(/\s+/g, ' ');
            // Include serviceType to allow multiple services at same address
            const serviceTypeId = service.serviceType?.id || service.serviceType?._id?.toString() || service.serviceType?.category || 'unknown';
            
            // Check if this is a seasonal contract
            const isSeasonal = acceptedBidId && service.acceptedBidId?.servicePricing?.some((sp: any) => sp.isSeasonalContract === true);
            
            // Primary key: customer + provider + address + serviceType (this allows multiple services at same address)
            const primaryKey = `${customerId}-${providerId}-${address}-${serviceTypeId}`;
            
            // Secondary key: customer + provider + acceptedBidId (for same bid)
            const secondaryKey = acceptedBidId ? `${customerId}-${providerId}-${acceptedBidId}` : null;
            
            // Check if we already have a service for this customer/provider/address
            let existingService = serviceMap.get(primaryKey);
            
            // Also check secondary key if it exists
            if (!existingService && secondaryKey) {
              existingService = serviceMap.get(secondaryKey);
              if (existingService) {
                // Move existing service to primary key
                serviceMap.delete(secondaryKey);
                serviceMap.set(primaryKey, existingService);
              }
            }
            
            if (!existingService) {
              // No existing service, add this one
              serviceMap.set(primaryKey, service);
              if (secondaryKey) {
                serviceMap.set(secondaryKey, service);
              }
            } else {
              // We have a duplicate - decide which one to keep
              const existingDate = existingService.createdAt ? new Date(existingService.createdAt) : null;
              const currentDate = service.createdAt ? new Date(service.createdAt) : null;
              
              const existingHasBid = !!(existingService.acceptedBidId);
              const currentHasBid = !!(service.acceptedBidId);
              
              // Check if either is a seasonal contract
              const existingIsSeasonal = existingHasBid && existingService.acceptedBidId?.servicePricing?.some((sp: any) => sp.isSeasonalContract === true);
              const currentIsSeasonal = isSeasonal;
              
              // Priority 1: Prefer seasonal contract over regular service
              if (currentIsSeasonal && !existingIsSeasonal) {
                serviceMap.set(primaryKey, service);
                if (secondaryKey) {
                  serviceMap.set(secondaryKey, service);
                }
              } else if (!currentIsSeasonal && existingIsSeasonal) {
                // Keep existing (it's seasonal)
              } else if (currentHasBid && !existingHasBid) {
                // Priority 2: Prefer service with acceptedBidId
                serviceMap.set(primaryKey, service);
                if (secondaryKey) {
                  serviceMap.set(secondaryKey, service);
                }
              } else if (!currentHasBid && existingHasBid) {
                // Keep existing
              } else if (currentDate && existingDate) {
                // Priority 3: Both have dates, prefer more recent
                if (currentDate > existingDate) {
                  serviceMap.set(primaryKey, service);
                  if (secondaryKey) {
                    serviceMap.set(secondaryKey, service);
                  }
                }
              } else if (currentDate && !existingDate) {
                // Current has date, existing doesn't
                serviceMap.set(primaryKey, service);
                if (secondaryKey) {
                  serviceMap.set(secondaryKey, service);
                }
              }
            }
          });
          
          // Convert map back to array - only use primary keys to avoid duplicates
          // Filter out services that are stored under secondary keys only
          const deduplicatedServices = Array.from(serviceMap.entries())
            .filter(([key, service]) => {
              // Only include services stored under primary keys (customer-provider-address-serviceType)
              const customerId = service.customerId?._id?.toString() || service.customerId?.toString() || '';
              const providerId = service.providerId?._id?.toString() || service.providerId?.toString() || '';
              // Normalize address: lowercase, trim, remove extra spaces
              const address = (service.address?.street || '').toLowerCase().trim().replace(/\s+/g, ' ');
              // Include serviceType to match the primary key format
              const serviceTypeId = service.serviceType?.id || service.serviceType?._id?.toString() || service.serviceType?.category || 'unknown';
              const primaryKey = `${customerId}-${providerId}-${address}-${serviceTypeId}`;
              return key === primaryKey;
            })
            .map(([key, service]) => service);
          
          console.log('[CUSTOMER_DASHBOARD] Services before deduplication:', services.length);
          console.log('[CUSTOMER_DASHBOARD] Services after deduplication:', deduplicatedServices.length);
          
          // Map services to match expected format
          // Expand services: create separate rows for each service in servicePricing
          const allServices: any[] = [];
          
          deduplicatedServices.forEach((service: any) => {
            // Get service details
            const serviceName = service.serviceType?.name || 'Service';
            const providerName = service.providerId?.businessName || 
                                `${service.providerId?.firstName || ''} ${service.providerId?.lastName || ''}`.trim() || 
                                'Provider';
            
            // Common provider contact info
            const providerContact = {
              name: `${service.providerId?.firstName || ''} ${service.providerId?.lastName || ''}`.trim(),
              businessName: service.providerId?.businessName,
              email: service.providerId?.email,
              phone: service.providerId?.phone,
              businessLogo: (service.providerId as any)?.businessLogo,
            };
            
            // Common date handling
            const lastServiceDate = service.lastServiceDate 
              ? (typeof service.lastServiceDate === 'string' 
                  ? service.lastServiceDate 
                  : service.lastServiceDate instanceof Date 
                    ? service.lastServiceDate.toISOString()
                    : new Date(service.lastServiceDate).toISOString())
              : (service.completedDate 
                  ? (typeof service.completedDate === 'string'
                      ? service.completedDate
                      : service.completedDate instanceof Date
                        ? service.completedDate.toISOString()
                        : new Date(service.completedDate).toISOString())
                  : null);
            
            const nextScheduledService = service.nextScheduledService 
              ? (typeof service.nextScheduledService === 'string' 
                  ? service.nextScheduledService 
                  : service.nextScheduledService instanceof Date 
                    ? service.nextScheduledService.toISOString()
                    : new Date(service.nextScheduledService).toISOString())
              : (service.scheduledDate 
                  ? (typeof service.scheduledDate === 'string'
                      ? service.scheduledDate
                      : service.scheduledDate instanceof Date
                        ? service.scheduledDate.toISOString()
                        : new Date(service.scheduledDate).toISOString())
                  : null);
            
            // Check if we have servicePricing array (multiple services)
            if (service.acceptedBidId?.servicePricing && Array.isArray(service.acceptedBidId.servicePricing) && service.acceptedBidId.servicePricing.length > 0) {
              console.log('[CUSTOMER_DASHBOARD] Found servicePricing array with', service.acceptedBidId.servicePricing.length, 'services');
              // Create a separate row for each service in servicePricing
              service.acceptedBidId.servicePricing.forEach((sp: any, index: number) => {
                let price = 'N/A';
                let frequency = sp.frequency || service.frequency || 'N/A';
                let isSeasonalContract = sp.isSeasonalContract === true;
                
                // Use the service name from servicePricing, not from serviceType
                // Map service type to display name if serviceName is not available
                let displayServiceName = sp.serviceName;
                if (!displayServiceName && sp.service) {
                  // Map service key to display name
                  const serviceNameMap: { [key: string]: string } = {
                    'lawnMowingTrimming': 'Lawn Mowing/Trimming',
                    'weedControlFertilizing': 'Weed Control/Fertilizing',
                    'snowRemoval': 'Snow Removal'
                  };
                  displayServiceName = serviceNameMap[sp.service] || sp.service;
                }
                if (!displayServiceName) {
                  displayServiceName = 'Service';
                }
                displayServiceName = normalizeServiceDisplayName(displayServiceName);
                
                console.log('[CUSTOMER_DASHBOARD] Creating row for service:', displayServiceName, 'Price:', sp.price);
                
                // Get price for this specific service
                if (isSeasonalContract && sp.seasonalContractDetails?.totalContractPrice) {
                  // For seasonal contracts, use total contract price
                  price = `$${parseFloat(sp.seasonalContractDetails.totalContractPrice).toFixed(2)}`;
                  frequency = 'seasonal-contract';
                } else if (sp.price) {
                  // Regular service price
                  price = `$${parseFloat(sp.price).toFixed(2)}`;
                }
                
                allServices.push({
                  ...service,
                  _id: `${service._id}_${index}`, // Unique ID for each service row (for React key)
                  serviceRequestId: service._id, // Preserve original service request ID for API calls
                  serviceName: displayServiceName,
                  providerName,
                  price,
                  frequency,
                  isSeasonalContract,
                  seasonalContractServiceFrequency: service.seasonalContractServiceFrequency,
                  seasonalContractDetails: sp.seasonalContractDetails || null, // Store full seasonal contract details
                  providerContact,
                  lastServiceDate,
                  nextScheduledService,
                });
              });
            } else {
              // Fallback: single service (no servicePricing array or old format)
              let price = 'N/A';
              let frequency = service.frequency || 'N/A';
              let isSeasonalContract = false;
              
              // Try to get pricing from accepted bid
              if (service.acceptedBidId) {
                if (service.acceptedBidId.price) {
                  price = `$${parseFloat(service.acceptedBidId.price).toFixed(2)}`;
                }
              } 
              // For onboarded customers, price is populated from ExistingCustomer.servicePricing
              else if (service.price !== undefined && service.price !== null) {
                price = `$${parseFloat(service.price.toString()).toFixed(2)}`;
              }
              // Fallback: if no acceptedBidId but has pricing info on service request itself
              else if (service.estimatedCost) {
                const avgPrice = (service.estimatedCost.min + service.estimatedCost.max) / 2;
                price = `$${avgPrice.toFixed(2)}`;
              }
              
              allServices.push({
                ...service,
                serviceName: normalizeServiceDisplayName(serviceName),
                providerName,
                price,
                frequency,
                isSeasonalContract,
                seasonalContractServiceFrequency: service.seasonalContractServiceFrequency,
                providerContact,
                lastServiceDate,
                nextScheduledService,
              });
            }
          });
          
          setServices(allServices);
          setLoadingServices(false);
          return;
        }
      } catch (err: any) {
        myServicesFailed = true;
        console.error('=== /my-services ENDPOINT FAILED ===');
        console.error('Error object:', err);
        console.error('Error message:', err?.message);
        console.error('Error response status:', err?.response?.status);
        console.error('Error response statusText:', err?.response?.statusText);
        console.error('Error response data:', err?.response?.data);
        console.error('Error response URL:', err?.response?.config?.url || err?.config?.url);
        console.error('Error stack:', err?.stack);
        console.error('[CUSTOMER_DASHBOARD] This means the /my-services endpoint failed');
        console.error('[CUSTOMER_DASHBOARD] Falling back to main endpoint...');
      }
      
      // Fallback to main endpoint (now has same query logic)
      if (myServicesFailed) {
        console.log('[CUSTOMER_DASHBOARD] Using fallback endpoint since /my-services failed');
      }
      console.log('[CUSTOMER_DASHBOARD] Calling fallback: GET /service-requests?userType=customer&limit=100');
      response = await apiService.get('/service-requests?userType=customer&limit=100');
      
      console.log('=== FETCH SERVICES DEBUG (FALLBACK ENDPOINT) ===');
      console.log('Full API response:', response);
      console.log('Response success:', response.success);
      console.log('Response data:', response.data);
      
      // Get all service requests from response
      const allServiceRequests = response.success && response.data
        ? (response.data as any).serviceRequests || []
        : [];
      
      console.log('Total service requests fetched:', allServiceRequests.length);
      console.log('All service requests:', allServiceRequests.map((s: any) => ({
        id: s._id || s.id,
        status: s.status,
        serviceType: s.serviceType?.name,
        provider: s.providerId?.businessName || s.providerId?.firstName,
        acceptedBidId: s.acceptedBidId?._id || s.acceptedBidId || 'none'
      })));
      
      // Filter to show current services: services with acceptedBidId OR services with providerId in active status
      // This matches the backend query logic - shows all current services from all providers
      // Include pending_cancellation and cancelled (show with "Pending Cancellation" / "Cancelled" badges in UI)
      const allServices = allServiceRequests.filter((service: any) => {
        // Option 1: Has an accepted bid (most common case)
        const hasAcceptedBid = !!(service.acceptedBidId);
        
        // Option 2: Has a provider assigned and is in an active or terminal status
        const hasProviderInActiveStatus = !!(service.providerId) && 
          ['accepted', 'scheduled', 'in-progress', 'paid', 'pending_cancellation', 'cancelled'].includes(service.status);
        
        return hasAcceptedBid || hasProviderInActiveStatus;
      });
      
      console.log('Filtered current services (with acceptedBidId OR provider in active status):', allServices.length);
      console.log('Current services:', allServices.map((s: any) => ({
        id: s._id || s.id,
        status: s.status,
        serviceType: s.serviceType?.name,
        hasAcceptedBid: !!s.acceptedBidId,
        hasProviderId: !!s.providerId
      })));
      console.log('=== END DEBUG ===');

      // Deduplicate services: If multiple services exist with the same acceptedBidId, they are duplicates
      // Also deduplicate services for the same customer/provider/address/serviceType combination, preferring seasonal contracts
      // IMPORTANT: Include serviceType in the key to allow multiple services at the same address
      const serviceMap = new Map<string, any>();
      allServices.forEach((service: any) => {
        const customerId = service.customerId?._id?.toString() || service.customerId?.toString() || '';
        const providerId = service.providerId?._id?.toString() || service.providerId?.toString() || '';
        const acceptedBidId = service.acceptedBidId?._id?.toString() || service.acceptedBidId?.toString() || '';
        // Normalize address: lowercase, trim, remove extra spaces
        const address = (service.address?.street || '').toLowerCase().trim().replace(/\s+/g, ' ');
        // Include serviceType to allow multiple services at same address
        const serviceTypeId = service.serviceType?.id || service.serviceType?._id?.toString() || service.serviceType?.category || 'unknown';
        
        // Check if this is a seasonal contract
        const isSeasonal = acceptedBidId && service.acceptedBidId?.servicePricing?.some((sp: any) => sp.isSeasonalContract === true);
        
        // Primary key: customer + provider + address + serviceType (this allows multiple services at same address)
        const primaryKey = `${customerId}-${providerId}-${address}-${serviceTypeId}`;
        
        // Secondary key: customer + provider + acceptedBidId (for same bid)
        const secondaryKey = acceptedBidId ? `${customerId}-${providerId}-${acceptedBidId}` : null;
        
        // Check if we already have a service for this customer/provider/address
        let existingService = serviceMap.get(primaryKey);
        
        // Also check secondary key if it exists
        if (!existingService && secondaryKey) {
          existingService = serviceMap.get(secondaryKey);
          if (existingService) {
            // Move existing service to primary key
            serviceMap.delete(secondaryKey);
            serviceMap.set(primaryKey, existingService);
          }
        }
        
        if (!existingService) {
          // No existing service, add this one
          serviceMap.set(primaryKey, service);
          if (secondaryKey) {
            serviceMap.set(secondaryKey, service);
          }
        } else {
          // We have a duplicate - decide which one to keep
          const existingDate = existingService.createdAt ? new Date(existingService.createdAt) : null;
          const currentDate = service.createdAt ? new Date(service.createdAt) : null;
          
          const existingHasBid = !!(existingService.acceptedBidId);
          const currentHasBid = !!(service.acceptedBidId);
          
          // Check if either is a seasonal contract
          const existingIsSeasonal = existingHasBid && existingService.acceptedBidId?.servicePricing?.some((sp: any) => sp.isSeasonalContract === true);
          const currentIsSeasonal = isSeasonal;
          
          // Priority 1: Prefer seasonal contract over regular service
          if (currentIsSeasonal && !existingIsSeasonal) {
            serviceMap.set(primaryKey, service);
            if (secondaryKey) {
              serviceMap.set(secondaryKey, service);
            }
          } else if (!currentIsSeasonal && existingIsSeasonal) {
            // Keep existing (it's seasonal)
          } else if (currentHasBid && !existingHasBid) {
            // Priority 2: Prefer service with acceptedBidId
            serviceMap.set(primaryKey, service);
            if (secondaryKey) {
              serviceMap.set(secondaryKey, service);
            }
          } else if (!currentHasBid && existingHasBid) {
            // Keep existing
          } else if (currentDate && existingDate) {
            // Priority 3: Both have dates, prefer more recent
            if (currentDate > existingDate) {
              serviceMap.set(primaryKey, service);
              if (secondaryKey) {
                serviceMap.set(secondaryKey, service);
              }
            }
          } else if (currentDate && !existingDate) {
            // Current has date, existing doesn't
            serviceMap.set(primaryKey, service);
            if (secondaryKey) {
              serviceMap.set(secondaryKey, service);
            }
          }
        }
      });
      
      // Convert map back to array - only use primary keys to avoid duplicates
      // Filter out services that are stored under secondary keys only
      const deduplicatedServices = Array.from(serviceMap.entries())
        .filter(([key, service]) => {
          // Only include services stored under primary keys (customer-provider-address-serviceType)
          const customerId = service.customerId?._id?.toString() || service.customerId?.toString() || '';
          const providerId = service.providerId?._id?.toString() || service.providerId?.toString() || '';
          // Normalize address: lowercase, trim, remove extra spaces
          const address = (service.address?.street || '').toLowerCase().trim().replace(/\s+/g, ' ');
          // Include serviceType to match the primary key format
          const serviceTypeId = service.serviceType?.id || service.serviceType?._id?.toString() || service.serviceType?.category || 'unknown';
          const primaryKey = `${customerId}-${providerId}-${address}-${serviceTypeId}`;
          return key === primaryKey;
        })
        .map(([key, service]) => service);
      
      console.log('[CUSTOMER_DASHBOARD] Services before deduplication (fallback):', allServices.length);
      console.log('[CUSTOMER_DASHBOARD] Services after deduplication (fallback):', deduplicatedServices.length);
      
      // Expand services: create separate rows for each service in servicePricing (fallback endpoint)
      const servicesWithHistory: any[] = [];
      
      deduplicatedServices.forEach((service: any) => {
        // Get service details from accepted bid
        const serviceName = service.serviceType?.name || 'Service';
        const providerName = service.providerId?.businessName || 
                            `${service.providerId?.firstName || ''} ${service.providerId?.lastName || ''}`.trim() || 
                            'Provider';
        
        // Common provider contact info
        const providerContact = {
          name: `${service.providerId?.firstName || ''} ${service.providerId?.lastName || ''}`.trim(),
          businessName: service.providerId?.businessName,
          email: service.providerId?.email,
          phone: service.providerId?.phone,
          businessLogo: (service.providerId as any)?.businessLogo,
        };
        
        // Common date handling (fallback endpoint doesn't provide enriched dates from service history)
        const lastServiceDate = service.lastServiceDate 
          ? (typeof service.lastServiceDate === 'string' 
              ? service.lastServiceDate 
              : service.lastServiceDate instanceof Date 
                ? service.lastServiceDate.toISOString()
                : new Date(service.lastServiceDate).toISOString())
          : (service.completedDate 
              ? (typeof service.completedDate === 'string'
                  ? service.completedDate
                  : service.completedDate instanceof Date
                    ? service.completedDate.toISOString()
                    : new Date(service.completedDate).toISOString())
              : null);
        
        const nextScheduledService = service.nextScheduledService 
          ? (typeof service.nextScheduledService === 'string' 
              ? service.nextScheduledService 
              : service.nextScheduledService instanceof Date 
                ? service.nextScheduledService.toISOString()
                : new Date(service.nextScheduledService).toISOString())
          : (service.scheduledDate 
              ? (typeof service.scheduledDate === 'string'
                  ? service.scheduledDate
                  : service.scheduledDate instanceof Date
                    ? service.scheduledDate.toISOString()
                    : new Date(service.scheduledDate).toISOString())
              : null);
        
        // Check if we have servicePricing array (multiple services)
        if (service.acceptedBidId?.servicePricing && Array.isArray(service.acceptedBidId.servicePricing) && service.acceptedBidId.servicePricing.length > 0) {
          console.log('[CUSTOMER_DASHBOARD] Found servicePricing array with', service.acceptedBidId.servicePricing.length, 'services (fallback)');
          // Create a separate row for each service in servicePricing
          service.acceptedBidId.servicePricing.forEach((sp: any, index: number) => {
            let price = 'N/A';
            let frequency = sp.frequency || service.frequency || 'N/A';
            let isSeasonalContract = sp.isSeasonalContract === true;
            
            // Use the service name from servicePricing, not from serviceType
            // Map service type to display name if serviceName is not available
            let displayServiceName = sp.serviceName;
            if (!displayServiceName && sp.service) {
              // Map service key to display name
              const serviceNameMap: { [key: string]: string } = {
                'lawnMowingTrimming': 'Lawn Mowing/Trimming',
                'weedControlFertilizing': 'Weed Control/Fertilizing',
                'snowRemoval': 'Snow Removal'
              };
              displayServiceName = serviceNameMap[sp.service] || sp.service;
            }
            if (!displayServiceName) {
              displayServiceName = 'Service';
            }
            displayServiceName = normalizeServiceDisplayName(displayServiceName);
            
            console.log('[CUSTOMER_DASHBOARD] Creating row for service (fallback):', displayServiceName, 'Price:', sp.price);
            
            // Get price for this specific service
            if (isSeasonalContract && sp.seasonalContractDetails?.totalContractPrice) {
              // For seasonal contracts, use total contract price
              price = `$${parseFloat(sp.seasonalContractDetails.totalContractPrice).toFixed(2)}`;
              frequency = 'seasonal-contract';
            } else if (sp.price) {
              // Regular service price
              price = `$${parseFloat(sp.price).toFixed(2)}`;
            }
            
            servicesWithHistory.push({
              ...service,
              _id: `${service._id}_${index}`, // Unique ID for each service row (for React key)
              serviceRequestId: service._id, // Preserve original service request ID for API calls
              serviceName: displayServiceName,
              providerName,
              price,
              frequency,
              isSeasonalContract,
              seasonalContractServiceFrequency: service.seasonalContractServiceFrequency,
              providerContact,
              lastServiceDate,
              nextScheduledService,
            });
          });
        } else {
          // Fallback: single service (no servicePricing array or old format)
          let price = 'N/A';
          let frequency = service.frequency || 'N/A';
          let isSeasonalContract = false;
          
          // Try to get pricing from accepted bid
          if (service.acceptedBidId) {
            if (service.acceptedBidId.price) {
              price = `$${parseFloat(service.acceptedBidId.price).toFixed(2)}`;
            }
          } 
          // For onboarded customers, price is populated from ExistingCustomer.servicePricing
          else if (service.price !== undefined && service.price !== null) {
            price = `$${parseFloat(service.price.toString()).toFixed(2)}`;
          }
          // Fallback: if no acceptedBidId but has pricing info on service request itself
          else if (service.estimatedCost) {
            const avgPrice = (service.estimatedCost.min + service.estimatedCost.max) / 2;
            price = `$${avgPrice.toFixed(2)}`;
          }
          
          servicesWithHistory.push({
            ...service,
            serviceName: normalizeServiceDisplayName(serviceName),
            providerName,
            price,
            frequency,
            isSeasonalContract,
            seasonalContractServiceFrequency: service.seasonalContractServiceFrequency,
            providerContact,
            lastServiceDate,
            nextScheduledService,
          });
        }
      });

      setServices(servicesWithHistory);
    } catch (err) {
      console.error('Failed to fetch services:', err);
      setServices([]);
    } finally {
      setLoadingServices(false);
    }
  };


  const fetchPaymentInfo = async () => {
    try {
      console.log('[CUSTOMER_DASHBOARD] Fetching payment info...');
      const response = await apiService.get('/payments/saved-methods');
      console.log('[CUSTOMER_DASHBOARD] Payment info response:', {
        success: response.success,
        hasData: !!response.data,
        isArray: Array.isArray(response.data),
        dataLength: Array.isArray(response.data) ? response.data.length : 0,
        data: response.data
      });
      
      if (response.success && response.data && Array.isArray(response.data)) {
        // Get the first active payment method
        const activePaymentMethod = response.data.find((method: any) => method.status === 'active');
        console.log('[CUSTOMER_DASHBOARD] Active payment method found:', {
          found: !!activePaymentMethod,
          method: activePaymentMethod,
          allMethods: response.data.map((m: any) => ({ id: m._id, status: m.status, cardLast4: m.cardLast4 }))
        });
        
        if (activePaymentMethod) {
          setPaymentInfo({
            hasActivePaymentMethod: true,
            cardCount: response.data.length,
            defaultCard: {
              cardLast4: activePaymentMethod.cardLast4,
              cardBrand: activePaymentMethod.cardBrand,
              cardExpMonth: activePaymentMethod.cardExpMonth,
              cardExpYear: activePaymentMethod.cardExpYear
            },
            lastUsedAt: activePaymentMethod.lastUsedAt,
            verificationStatus: activePaymentMethod.verificationStatus,
            verificationDate: activePaymentMethod.verificationDate,
            verificationAmount: activePaymentMethod.verificationAmount,
            cardLast4: activePaymentMethod.cardLast4,
            cardBrand: activePaymentMethod.cardBrand
          });
          console.log('[CUSTOMER_DASHBOARD] Payment info set to: hasActivePaymentMethod = true');
        } else {
          setPaymentInfo({
            hasActivePaymentMethod: false,
            cardCount: response.data.length
          });
          console.log('[CUSTOMER_DASHBOARD] Payment info set to: hasActivePaymentMethod = false (no active method found)');
        }
      } else {
        setPaymentInfo({
          hasActivePaymentMethod: false,
          cardCount: 0
        });
        console.log('[CUSTOMER_DASHBOARD] Payment info set to: hasActivePaymentMethod = false (invalid response)');
      }
    } catch (err) {
      console.error('[CUSTOMER_DASHBOARD] Failed to fetch payment info:', err);
      setPaymentInfo({
        hasActivePaymentMethod: false,
        cardCount: 0
      });
    } finally {
      setLoadingPaymentInfo(false);
    }
  };


  const fetchOnboardingOffers = async () => {
    try {
      setLoadingOnboarding(true);
      const response = await apiService.get('/existing-customers/onboard/pending');
      if (response.success && response.data && Array.isArray(response.data)) {
        setOnboardingOffers(response.data);
      } else {
        setOnboardingOffers([]);
      }
    } catch (err) {
      console.error('Failed to fetch onboarding offers:', err);
      setOnboardingOffers([]);
    } finally {
      setLoadingOnboarding(false);
    }
  };

  const formatPropertyType = (propertyType: string) => {
    if (!propertyType) return 'N/A';
    
    const typeMap: { [key: string]: string } = {
      'single_residential': 'Single Family Residential',
      'multi_family_condo': 'Multi-Family / Condo',
      'commercial': 'Commercial',
      'vacant_lot': 'Vacant Lot',
      'hoa_managed': 'HOA-Managed Property',
    };
    
    return typeMap[propertyType] || propertyType;
  };


  const handleAcceptServices = async (service: any) => {
    try {
      setLoading(true);
      setError('');
      
      // Call the setup payment account endpoint to get Stripe URL
      const response = await apiService.post('/payments/setup-payment-account', {});
      
      if (response.success && (response as any).paymentPageUrl) {
        // Redirect directly to Stripe payment page
        window.location.href = (response as any).paymentPageUrl;
      } else {
        setError((response as any).message || 'Failed to setup payment account');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to accept services');
    } finally {
      setLoading(false);
    }
  };

  const handleDeclineServices = (service: any) => {
    setDeclineDialog({ open: true, service });
  };

  const handleConfirmDecline = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Remove the service from the list and notify provider
      setServices(services.filter(s => s._id !== declineDialog.service._id));
      setSuccess('Services declined. You will now be redirected to create a new service request.');
      
      // Close the dialog
      setDeclineDialog({ open: false, service: null });
      
      // Redirect to service request page after a short delay
      setTimeout(() => {
        navigate('/service-request');
      }, 2000);
      
    } catch (err: any) {
      setError(err.message || 'Failed to decline services');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDecline = () => {
    setDeclineDialog({ open: false, service: null });
  };

  const handleCancelService = (service: any) => {
    setDeleteDialog({ open: true, service });
    setCancellationReason('');
    setError('');
  };

  const handleCloseProviderContactDialog = () => {
    setProviderContactDialog({ open: false, provider: null });
  };

  const handleConfirmCancel = async () => {
    if (!deleteDialog.service) return;

    try {
      setDeleting(true);
      setError('');
      
      // Use serviceRequestId if available (for services with composite _id), otherwise use _id
      const serviceRequestId = deleteDialog.service.serviceRequestId || deleteDialog.service._id;
      
      // Extract original ID if it's a composite ID (has underscore)
      const originalId = typeof serviceRequestId === 'string' && serviceRequestId.includes('_')
        ? serviceRequestId.split('_')[0]
        : serviceRequestId;
      
      // Cancel the service request (reason required)
      const response = await apiService.post(`/service-requests/${originalId}/cancel`, {
        cancellationReason: cancellationReason.trim(),
      });
      
      if (response.success) {
        setSuccess(response.message || 'Service cancelled successfully');
        if ((response as any).warnings && Array.isArray((response as any).warnings) && (response as any).warnings.length > 0) {
          setSuccess(`${response.message || 'Service cancelled successfully'}. ${(response as any).warnings.join(' ')}`);
        }
        setDeleteDialog({ open: false, service: null });
        setCancellationReason(''); // Reset cancellation reason after successful cancellation
        // Refresh services list
        await fetchServices();
      } else {
        setError(response.error || 'Failed to cancel service');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to cancel service');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialog({ open: false, service: null });
    setCancellationReason('');
    setError('');
  };

  // Format date as MM/DD/YYYY
  // IMPORTANT: Dates stored as UTC midnight (e.g., 2025-11-14T00:00:00.000Z) represent calendar dates
  // We should interpret the UTC date components directly, not convert timezones
  const formatDate = (dateInput: Date | null | string): string => {
    if (!dateInput) return 'N/A';
    
    try {
      let dateObj: Date;
      let dateString: string;
      
      if (dateInput instanceof Date) {
        dateObj = dateInput;
        // Extract UTC date components directly (this represents the calendar date)
        const year = dateObj.getUTCFullYear();
        const month = dateObj.getUTCMonth() + 1; // getUTCMonth() returns 0-11
        const day = dateObj.getUTCDate();
        
        // Format as MM/DD/YYYY
        return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
      } else {
        // It's a string - parse it to extract date components
        dateString = dateInput.toString().trim();
        
        // If it's already in YYYY-MM-DD format
        if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = dateString.split('-');
          return `${month}/${day}/${year}`;
        }
        
        // If it's an ISO string (YYYY-MM-DDTHH:mm:ss.sssZ), extract the date part
        if (dateString.includes('T')) {
          try {
            const dateOnly = dateString.split('T')[0]; // Get YYYY-MM-DD part
            const [year, month, day] = dateOnly.split('-');
            return `${month}/${day}/${year}`;
          } catch (e) {
            // Fallback: parse as Date and extract UTC components
            dateObj = new Date(dateString);
            if (!isNaN(dateObj.getTime())) {
              const year = dateObj.getUTCFullYear();
              const month = dateObj.getUTCMonth() + 1;
              const day = dateObj.getUTCDate();
              return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
            }
          }
        } else {
          // Try to parse as Date and extract UTC components
          dateObj = new Date(dateString);
          if (!isNaN(dateObj.getTime())) {
            const year = dateObj.getUTCFullYear();
            const month = dateObj.getUTCMonth() + 1;
            const day = dateObj.getUTCDate();
            return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
          }
        }
      }
      
      return 'Invalid date';
    } catch (e) {
      console.error('[CustomerDashboard] Error formatting date:', dateInput, e);
      return 'Invalid date';
    }
  };

  const formatFrequency = (freq: string): string => {
    const freqMap: { [key: string]: string } = {
      'weekly': 'Weekly',
      'bi-weekly': 'Bi-weekly',
      'monthly': 'Monthly',
      'per-instance': 'Per Instance',
      'seasonal-contract': 'Seasonal Contract',
    };
    return freqMap[freq] || freq;
  };

  const handlePaymentSetupSuccess = () => {
    setPaymentSetupDialog({ 
      open: false, 
      serviceId: '', 
      providerId: '', 
      services: [], 
      servicePricing: [], 
      totalPrice: 0 
    });
    setSuccess('Payment method set up successfully! Your services have been accepted.');
    // Refresh the services list and onboarding offers
    fetchServices();
    fetchPaymentInfo();
    fetchOnboardingOffers();
  };

  const handlePaymentSetupClose = () => {
    setPaymentSetupDialog({ 
      open: false, 
      serviceId: '', 
      providerId: '', 
      services: [], 
      servicePricing: [], 
      totalPrice: 0 
    });
  };

  const handleSetupPaymentAccount = async () => {
    try {
      setSetupPaymentLoading(true);
      setError('');

      const response = await apiService.post('/payments/setup-payment-account', {});
      
      if (response.success && (response as any).paymentPageUrl) {
        // Redirect to Stripe payment page
        window.location.href = (response as any).paymentPageUrl;
      } else {
        setError((response as any).message || 'Failed to setup payment account');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to setup payment account');
    } finally {
      setSetupPaymentLoading(false);
    }
  };

  const handleAcceptOnboarding = async (offer: any) => {
    // Check if offer has seasonal contracts
    const hasSeasonalContract = offer.servicePricing && (
      (offer.servicePricing.lawnMowingTrimming && (offer.servicePricing.lawnMowingTrimming.isSeasonalContract || offer.servicePricing.lawnMowingTrimming.frequency === 'seasonal-contract')) ||
      (offer.servicePricing.weedControlFertilizing && (offer.servicePricing.weedControlFertilizing.isSeasonalContract || offer.servicePricing.weedControlFertilizing.frequency === 'seasonal-contract')) ||
      (offer.servicePricing.snowRemoval && (offer.servicePricing.snowRemoval.isSeasonalContract || offer.servicePricing.snowRemoval.frequency === 'seasonal-contract'))
    );

    if (hasSeasonalContract) {
      // Show seasonal contract confirmation dialog first
      setSeasonalContractConfirmDialog({ open: true, offer });
    } else {
      // No seasonal contract - proceed directly to payment setup
      proceedWithOnboardingPayment(offer);
    }
  };

  const proceedWithOnboardingPayment = async (offer: any) => {
    const agreementsSigned = await requestSignatures({ existingCustomerId: offer.existingCustomerId });
    if (!agreementsSigned) return;
    try {
      setLoading(true);
      setError('');
      
      // Accept the onboarding offer
      const response = await apiService.post(`/existing-customers/onboard/${offer.existingCustomerId}/accept`, {});
      
      if (response.success) {
        // Mark this as onboarding so PaymentSuccessPage knows to call the right endpoint
        sessionStorage.setItem('isOnboarding', 'true');
        sessionStorage.setItem('onboardingExistingCustomerId', offer.existingCustomerId); // NEW: Store property ID
        
        // Open Stripe payment setup - pass existingCustomerId to link payment method to property
        const paymentResponse = await apiService.post('/payments/setup-payment-account', {
          existingCustomerId: offer.existingCustomerId, // NEW: Link payment method to this property
        });
        if (paymentResponse.success && (paymentResponse as any).paymentPageUrl) {
          window.location.href = (paymentResponse as any).paymentPageUrl;
        } else {
          sessionStorage.removeItem('isOnboarding');
          setError('Failed to setup payment. Please try again.');
        }
      } else {
        setError(response.error || 'Failed to accept onboarding');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to accept onboarding');
    } finally {
      setLoading(false);
    }
  };

  const handleDeclineOnboarding = (offer: any) => {
    setOnboardingDeclineDialog({ open: true, offer });
  };

  const handleConfirmDeclineOnboarding = async () => {
    if (!onboardingDeclineDialog.offer) return;
    
    try {
      setLoading(true);
      setError('');
      
      const response = await apiService.post(`/existing-customers/onboard/${onboardingDeclineDialog.offer.existingCustomerId}/decline`, {});
      
      if (response.success) {
        setSuccess('Onboarding declined. Future service requests will be subject to 5% marketplace fees.');
        setOnboardingDeclineDialog({ open: false, offer: null });
        await fetchOnboardingOffers();
        await fetchServices();
      } else {
        setError(response.error || 'Failed to decline onboarding');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to decline onboarding');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDeclineOnboarding = () => {
    setOnboardingDeclineDialog({ open: false, offer: null });
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Seasonal Renewal Banner */}
      <SeasonalRenewalBanner />
      
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Welcome back, {user?.firstName}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your service requests and view your services
        </Typography>
      </Box>

      {/* Alert messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2, '& .MuiAlert-message': { fontWeight: 'bold', fontSize: '1.2rem' } }}
          onClose={() => setSuccess('')}
        >
          {success}
        </Alert>
      )}
      
      {/* Payment success toast notification */}
      <Snackbar
        open={paymentSuccessToast}
        autoHideDuration={6000}
        onClose={() => setPaymentSuccessToast(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setPaymentSuccessToast(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          Payment successful! Your service is confirmed.
        </Alert>
      </Snackbar>

      {/* Pending Onboarding Offers Section */}
      {loadingOnboarding ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : onboardingOffers.filter((offer: any) => {
        // Only show offers where payment hasn't been set up
        // If customer already has active payment method, hide the offer
        return !paymentInfo?.hasActivePaymentMethod;
      }).length > 0 ? (
        <Box sx={{ mt: 4 }} ref={onboardingSectionRef}>
          <Typography variant="h5" gutterBottom>
            Provider Service Offer
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your provider has offered to onboard you to LawnHub. Review the details below and accept to get started.
          </Typography>
          
          {onboardingOffers.filter((offer: any) => {
            // Only show offers where payment hasn't been set up
            return !paymentInfo?.hasActivePaymentMethod;
          }).map((offer: any) => (
            <Card key={offer.existingCustomerId} sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {offer.providerName}
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Address:</strong> {offer.address?.street || ''}, {offer.address?.city || ''}, {offer.address?.state || ''} {offer.address?.zipCode || ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Provider Contact:</strong> {offer.providerContact || 'N/A'}
                  </Typography>
                </Box>

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Services Offered:
                </Typography>
                <Box sx={{ ml: 2, mb: 2 }}>
                  {(() => {
                    // Helper function to render service details with monthly payment info
                    const renderServiceDetails = (
                      serviceKey: string,
                      serviceName: string,
                      servicePricing: any
                    ) => {
                      if (!servicePricing) return null;
                      
                      const isSeasonalContract = servicePricing.isSeasonalContract || servicePricing.frequency === 'seasonal-contract';
                      const seasonalDetails = servicePricing.seasonalContractDetails;
                      const paymentSchedule = seasonalDetails?.paymentSchedule || 'single-payment';
                      
                      // Calculate monthly payment amount and first payment date if monthly payments
                      // CRITICAL: Match backend logic exactly (subscriptionService.calculateMonthlyPaymentAmount and billing_cycle_anchor)
                      let monthlyPaymentAmount: number | undefined = undefined;
                      let firstPaymentDate: Date | undefined = undefined;
                      if (paymentSchedule === 'monthly' && seasonalDetails?.totalContractPrice && seasonalDetails?.contractStartDate && seasonalDetails?.contractEndDate) {
                        try {
                          const start = new Date(seasonalDetails.contractStartDate);
                          const end = new Date(seasonalDetails.contractEndDate);
                          // Use UTC date component for comparison (matches backend logic)
                          const startsOnFirst = start.getUTCDate() === 1;
                          
                          let billingMonths: number;
                          if (startsOnFirst) {
                            // Contract starts on 1st: Include that month in count - use UTC components
                            // Example: Feb 1 - Oct 31 = 9 months (Feb, Mar, Apr, May, Jun, Jul, Aug, Sep, Oct)
                            billingMonths = (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + 
                                          (end.getUTCMonth() - start.getUTCMonth()) + 1; // +1 to include both start and end months
                          } else {
                            // Contract starts mid-month: Exclude partial first month - use UTC components
                            // Example: Jan 6 - Oct 31 = 9 full months (Feb, Mar, Apr, May, Jun, Jul, Aug, Sep, Oct)
                            // Start from next month's 1st
                            const nextMonthYear = start.getUTCMonth() === 11 ? start.getUTCFullYear() + 1 : start.getUTCFullYear();
                            const nextMonthMonth = (start.getUTCMonth() + 1) % 12;
                            billingMonths = (end.getUTCFullYear() - nextMonthYear) * 12 + 
                                          (end.getUTCMonth() - nextMonthMonth) + 1; // +1 to include end month
                          }
                          
                          if (billingMonths > 0) {
                            // Round to 2 decimal places (matches backend)
                            monthlyPaymentAmount = Math.round((seasonalDetails.totalContractPrice / billingMonths) * 100) / 100;
                            
                            // Calculate first payment date (billing cycle anchor - matches backend logic) - use UTC components
                            if (startsOnFirst) {
                              // Contract starts on 1st: Use that 1st - use UTC components
                              firstPaymentDate = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
                            } else {
                              // Contract starts mid-month: Use next 1st of month - use UTC components
                              const nextMonthYear = start.getUTCMonth() === 11 ? start.getUTCFullYear() + 1 : start.getUTCFullYear();
                              const nextMonthMonth = (start.getUTCMonth() + 1) % 12;
                              firstPaymentDate = new Date(Date.UTC(nextMonthYear, nextMonthMonth, 1));
                            }
                          }
                        } catch (e) {
                          console.error('[ONBOARDING-OFFER] Error calculating monthly payment:', e);
                        }
                      }
                      
                      return (
                        <Box key={serviceKey} sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="body2" component="span" fontWeight="medium">
                              • {serviceName}:
                            </Typography>
                            <Typography variant="body2" component="span" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                              {isSeasonalContract && seasonalDetails?.totalContractPrice ? 
                                `$${parseFloat(seasonalDetails.totalContractPrice.toString()).toFixed(2)}` : 
                                servicePricing.price ? 
                                `$${parseFloat(servicePricing.price.toString()).toFixed(2)}` : 
                                'Price on quote'
                              }
                              {isSeasonalContract && seasonalDetails?.totalContractPrice && (
                                <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary', fontWeight: 'normal' }}>
                                  (Total Contract)
                                </Typography>
                              )}
                            </Typography>
                            {servicePricing.frequency && (
                              <Chip 
                                label={formatFrequency(servicePricing.frequency)} 
                                size="small" 
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </Box>
                          
                          {/* Seasonal Contract Details */}
                          {isSeasonalContract && seasonalDetails && (
                            <Box sx={{ ml: 2, mt: 1 }}>
                              {seasonalDetails.serviceFrequency && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Service Frequency: <strong>{formatFrequency(seasonalDetails.serviceFrequency)}</strong>
                                </Typography>
                              )}
                              {seasonalDetails.contractStartDate && seasonalDetails.contractEndDate && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Contract Period: <strong>
                                    {formatUTCDateString(seasonalDetails.contractStartDate)} - {formatUTCDateString(seasonalDetails.contractEndDate)}
                                  </strong>
                                </Typography>
                              )}
                              {paymentSchedule === 'monthly' && monthlyPaymentAmount && firstPaymentDate && (
                                <Box sx={{ mt: 1, p: 1, bgcolor: 'primary.light', borderRadius: 1 }}>
                                  <Typography variant="body2" fontWeight="bold" color="primary.dark">
                                    Monthly Payment Plan
                                  </Typography>
                                  <Typography variant="body2" fontWeight="medium" color="primary.dark">
                                    ${monthlyPaymentAmount.toFixed(2)} per month
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    First Payment: <strong>{formatUTCDateString(firstPaymentDate)}</strong>
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    Charged automatically on the 1st of each month thereafter
                                  </Typography>
                                </Box>
                              )}
                              {paymentSchedule === 'single-payment' && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    Payment: <strong>One-Time Payment (full payment after first service)</strong>
                                  </Typography>
                                  {seasonalDetails.contractStartDate && seasonalDetails.contractEndDate && (
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                      Contract Period: <strong>{formatUTCDateString(seasonalDetails.contractStartDate)} - {formatUTCDateString(seasonalDetails.contractEndDate)}</strong>
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      );
                    };
                    
                    return (
                      <>
                        {offer.services?.lawnMowingTrimming && renderServiceDetails(
                          'lawnMowingTrimming',
                          'Lawn Mowing/Trimming',
                          offer.servicePricing?.lawnMowingTrimming
                        )}
                        {offer.services?.weedControlFertilizing && renderServiceDetails(
                          'weedControlFertilizing',
                          'Weed Control/Fertilizer',
                          offer.servicePricing?.weedControlFertilizing
                        )}
                        {offer.services?.snowRemoval && renderServiceDetails(
                          'snowRemoval',
                          'Snow Removal',
                          offer.servicePricing?.snowRemoval
                        )}
                      </>
                    );
                  })()}
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleAcceptOnboarding(offer)}
                    disabled={loading}
                  >
                    Accept & Setup Payment
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleDeclineOnboarding(offer)}
                    disabled={loading}
                  >
                    Decline
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : null}

      <CustomerAgreementsCard />

      <CustomerAdditionalServicesEstimates />

      {/* My Services Section */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          My Services
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          View and manage your current services from all providers
        </Typography>
        
        {loadingServices ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : services.length === 0 ? (
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No active services. Accept a bid to see your services here!
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="40px"></TableCell>
                  <TableCell><strong>Service</strong></TableCell>
                  <TableCell><strong>Provider</strong></TableCell>
                  <TableCell><strong>Address</strong></TableCell>
                  <TableCell><strong>Price</strong></TableCell>
                  <TableCell><strong>Frequency</strong></TableCell>
                  <TableCell><strong>Last Service Date</strong></TableCell>
                  <TableCell><strong>Next Scheduled Service</strong></TableCell>
                  <TableCell align="center"><strong>Request Cancellation</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {services.map((service: any) => {
                  const isExpanded = expandedServiceRows.has(service._id);
                  const seasonalDetails = service.seasonalContractDetails;
                  const paymentSchedule = seasonalDetails?.paymentSchedule || 'single-payment';
                  
                  // Calculate monthly payment info if monthly
                  let monthlyPaymentInfo: { monthlyAmount: number; firstPaymentDate: Date; billingMonths: number } | null = null;
                  if (service.isSeasonalContract && paymentSchedule === 'monthly' && seasonalDetails?.totalContractPrice && seasonalDetails?.contractStartDate && seasonalDetails?.contractEndDate) {
                    const contractStart = new Date(seasonalDetails.contractStartDate);
                    const contractEnd = new Date(seasonalDetails.contractEndDate);
                    const monthsDiff = (contractEnd.getFullYear() - contractStart.getFullYear()) * 12 +
                                      (contractEnd.getMonth() - contractStart.getMonth());
                    const billingMonths = Math.max(1, monthsDiff);
                    const monthlyAmount = seasonalDetails.totalContractPrice / billingMonths;
                    monthlyPaymentInfo = {
                      monthlyAmount,
                      firstPaymentDate: contractStart,
                      billingMonths
                    };
                  }

                  return (
                    <React.Fragment key={service._id}>
                      <TableRow hover>
                        <TableCell>
                          {service.isSeasonalContract && seasonalDetails ? (
                            <IconButton
                              size="small"
                              onClick={() => {
                                const newExpanded = new Set(expandedServiceRows);
                                if (isExpanded) {
                                  newExpanded.delete(service._id);
                                } else {
                                  newExpanded.add(service._id);
                                }
                                setExpandedServiceRows(newExpanded);
                              }}
                            >
                              {isExpanded ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          {normalizeServiceDisplayName(service.serviceName || service.serviceType?.name || 'Service')}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="text"
                            onClick={() =>
                              setProviderContactDialog({
                                open: true,
                                provider: {
                                  name: service.providerContact?.name || service.providerName || 'Provider',
                                  businessName: service.providerContact?.businessName,
                                  email: service.providerContact?.email,
                                  phone: service.providerContact?.phone,
                                  businessLogo: service.providerContact?.businessLogo || (service.providerId as any)?.businessLogo,
                                },
                              })
                            }
                            sx={{ textTransform: 'none', padding: 0 }}
                          >
                            {service.providerName || 'Provider'}
                          </Button>
                        </TableCell>
                        <TableCell>
                          {service.address ? (
                            `${service.address.street || ''}, ${service.address.city || ''}, ${service.address.state || ''} ${service.address.zipCode || ''}`.trim()
                          ) : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {service.price || 'N/A'}
                        </TableCell>
                        <TableCell>
                          {service.isSeasonalContract && service.seasonalContractServiceFrequency ? (
                            <Box>
                              <Typography variant="body2" component="span">
                                Seasonal Contract
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                ({formatFrequency(service.seasonalContractServiceFrequency)})
                              </Typography>
                            </Box>
                          ) : (
                            formatFrequency(service.frequency)
                          )}
                        </TableCell>
                        <TableCell>
                          {formatDate(service.lastServiceDate)}
                        </TableCell>
                        <TableCell>
                          {service.nextScheduledService 
                            ? (() => {
                                // Handle both string (ISO) and Date object formats
                                const dateValue = typeof service.nextScheduledService === 'string' 
                                  ? service.nextScheduledService 
                                  : service.nextScheduledService instanceof Date
                                    ? service.nextScheduledService.toISOString()
                                    : service.nextScheduledService;
                                return formatDate(dateValue);
                              })()
                            : service.status === 'accepted' 
                              ? <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  Awaiting provider to schedule
                                </Typography>
                              : 'N/A'}
                        </TableCell>
                        <TableCell align="center">
                          {service.status === 'pending_cancellation' ? (
                            <Chip
                              label="Pending Cancellation"
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          ) : service.status === 'cancelled' ? (
                            <Chip
                              label="Cancelled"
                              size="small"
                              color="default"
                              variant="outlined"
                            />
                          ) : (
                            <IconButton
                              color="error"
                              onClick={() => handleCancelService(service)}
                              size="small"
                              title="Cancel Service"
                            >
                              <Delete />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                      {/* Expandable row for seasonal contract details */}
                      {service.isSeasonalContract && seasonalDetails && isExpanded && (
                        <TableRow>
                          <TableCell colSpan={9} sx={{ backgroundColor: 'primary.50', py: 2 }}>
                            <Box sx={{ pl: 3 }}>
                              <Typography variant="subtitle2" fontWeight="bold" color="primary.dark" gutterBottom>
                                Seasonal Contract Details
                              </Typography>
                              
                              {/* Contract Period */}
                              {seasonalDetails.contractStartDate && seasonalDetails.contractEndDate && (
                                <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                                  <strong>Contract Period:</strong> {formatUTCDateString(seasonalDetails.contractStartDate)} - {formatUTCDateString(seasonalDetails.contractEndDate)}
                                </Typography>
                              )}

                              {/* Total Contract Price */}
                              {seasonalDetails.totalContractPrice && (
                                <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                                  <strong>Total Contract Price:</strong> ${seasonalDetails.totalContractPrice.toFixed(2)}
                                </Typography>
                              )}

                              {/* Payment Schedule */}
                              {paymentSchedule === 'monthly' && monthlyPaymentInfo ? (
                                <Box sx={{ mt: 1, p: 1.5, backgroundColor: 'white', borderRadius: 1, border: 1, borderColor: 'primary.200' }}>
                                  <Typography variant="body2" fontWeight="medium" color="primary.dark" gutterBottom>
                                    Monthly Payment Plan
                                  </Typography>
                                  <Typography variant="body2" color="text.primary">
                                    <strong>${monthlyPaymentInfo.monthlyAmount.toFixed(2)}</strong> per month
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    First Payment: <strong>{formatUTCDateString(monthlyPaymentInfo.firstPaymentDate)}</strong>
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
                                  <strong>Payment Schedule:</strong> One-Time Payment (Full payment after first service)
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Payment Verification Status - Shown after services for onboarded customers */}
      {!loadingPaymentInfo && (
        <Box sx={{ mb: 4, mt: 4 }}>
          <CustomerPaymentInfo paymentMethodDetails={paymentInfo} />
          
          {/* Setup Payment Account Button */}
          <Box sx={{ mt: 2 }}>
            {!paymentInfo?.hasActivePaymentMethod ? (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {services.length > 0 
                    ? "You need to set up your payment method to complete your service setup."
                    : "* You must submit a service request and accept a bid before setting up your payment account."
                  }
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSetupPaymentAccount}
                  disabled={setupPaymentLoading || services.length === 0}
                  sx={{ minWidth: 200 }}
                >
                  {setupPaymentLoading ? 'Setting up...' : 'Setup Payment Account'}
                </Button>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="body2" color="success.main">
                  ✓ Payment method is set up and ready
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Provider Contact Dialog */}
      <Dialog
        open={providerContactDialog.open}
        onClose={handleCloseProviderContactDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Provider Contact Information</DialogTitle>
        <DialogContent dividers>
          {providerContactDialog.provider ? (
            <Box sx={{ display: 'flex', gap: 3 }}>
              {/* Contact Info on Left */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                {providerContactDialog.provider.businessName && (
                  <Typography variant="h6">{providerContactDialog.provider.businessName}</Typography>
                )}
                {providerContactDialog.provider.name && (
                  <Typography variant="body1">
                    <strong>Contact:</strong> {providerContactDialog.provider.name}
                  </Typography>
                )}
                {providerContactDialog.provider.email ? (
                  <Typography variant="body1">
                    <strong>Email:</strong>{' '}
                    <Button
                      component="a"
                      href={`mailto:${providerContactDialog.provider.email}`}
                      sx={{ textTransform: 'none', p: 0 }}
                    >
                      {providerContactDialog.provider.email}
                    </Button>
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Email not available
                  </Typography>
                )}
                {providerContactDialog.provider.phone ? (
                  <Typography variant="body1">
                    <strong>Phone:</strong>{' '}
                    <Button
                      component="a"
                      href={`tel:${providerContactDialog.provider.phone}`}
                      sx={{ textTransform: 'none', p: 0 }}
                    >
                      {providerContactDialog.provider.phone}
                    </Button>
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Phone number not available
                  </Typography>
                )}
              </Box>
              {/* Business Logo on Right */}
              {providerContactDialog.provider.businessLogo && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', flexShrink: 0 }}>
                  <Box
                    component="img"
                    src={providerContactDialog.provider.businessLogo}
                    alt={providerContactDialog.provider.businessName || 'Provider Logo'}
                    sx={{
                      maxWidth: 150,
                      maxHeight: 150,
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                </Box>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Provider contact information is not available.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProviderContactDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Service Decline Confirmation Dialog */}
      <Dialog
        open={declineDialog.open}
        onClose={handleCancelDecline}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Decline Provider Services</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            You are about to decline the services offered by your provider. Please read the following information carefully:
          </DialogContentText>
          
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Important Notice
            </Typography>
            <Typography variant="body2" component="div">
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li>If you decline these services, you will be redirected to create a new service request</li>
                <li>There is <strong>no guarantee</strong> that new bids will be the same price or lower than your current provider's offer</li>
                <li>You will <strong>not be able to go back</strong> and accept your provider's original pricing after declining</li>
                <li>New service requests will be subject to a <strong>5% platform fee</strong> on the service price (vs. 1% with your current provider)</li>
              </ul>
            </Typography>
          </Alert>

          {declineDialog.service && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                Current Provider Offer
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Provider:</strong> {declineDialog.service.providerId?.businessName || 'Provider'}
              </Typography>
              {declineDialog.service.services && (
                <Box sx={{ mt: 1 }}>
                  {declineDialog.service.services.lawnMowingTrimming && (
                    <Typography variant="body2">
                      • Lawn Mowing/Trimming: {declineDialog.service.servicePricing?.lawnMowingTrimming?.price ? 
                        `$${declineDialog.service.servicePricing.lawnMowingTrimming.price} (${declineDialog.service.servicePricing.lawnMowingTrimming.frequency})` : 
                        'Pricing not available'
                      }
                    </Typography>
                  )}
                  {declineDialog.service.services.weedControlFertilizing && (
                    <Typography variant="body2">
                      • Weed Control/Fertilizer: {declineDialog.service.servicePricing?.weedControlFertilizing?.price ? 
                        `$${declineDialog.service.servicePricing.weedControlFertilizing.price} (${declineDialog.service.servicePricing.weedControlFertilizing.frequency})` : 
                        'Pricing not available'
                      }
                    </Typography>
                  )}
                  {declineDialog.service.services.snowRemoval && (
                    <Typography variant="body2">
                      • Snow Removal: {declineDialog.service.servicePricing?.snowRemoval?.price ? 
                        `$${declineDialog.service.servicePricing.snowRemoval.price} (${declineDialog.service.servicePricing.snowRemoval.frequency})` : 
                        'Pricing not available'
                      }
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          <Alert severity="info">
            <Typography variant="body2">
              <strong>Fee Structure:</strong> Staying with your current provider maintains a 1% platform fee on the service price.
              New marketplace service requests use a 5% platform fee (first season). Your card is also charged a separate payment processing fee on top of the service price.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCancelDecline}
            disabled={loading}
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDecline}
            variant="contained"
            color="warning"
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Decline Services & Create New Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Service Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Service</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to cancel this service? Your provider will be notified immediately.
          </DialogContentText>
          {deleteDialog.service && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Service:</strong> {normalizeServiceDisplayName(deleteDialog.service.serviceName || deleteDialog.service.serviceType?.name || 'Service')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Provider:</strong> {deleteDialog.service.providerName || 'Provider'}
              </Typography>
              <Typography variant="body2">
                <strong>Address:</strong> {deleteDialog.service.address?.street}, {deleteDialog.service.address?.city}, {deleteDialog.service.address?.state} {deleteDialog.service.address?.zipCode}
              </Typography>
            </Box>
          )}
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>What happens when you cancel:</AlertTitle>
            <Typography variant="body2" component="div">
              • Your provider will be notified immediately<br/>
              • All future scheduled services will be cancelled<br/>
              • Service records will be preserved for billing and legal purposes<br/>
              • If you cancel after {cancellationCutoffText}, the upcoming service may still be chargeable
            </Typography>
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={3}
            required
            label="Reason for Cancellation"
            placeholder="Please provide a reason for cancelling this service..."
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            margin="normal"
            sx={{ mt: 2 }}
            helperText="Required. Your provider and LawnHub will be notified with this reason."
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCancelDelete}
            disabled={deleting}
          >
            Keep Service
          </Button>
          <Button 
            onClick={handleConfirmCancel}
            variant="contained"
            color="error"
            disabled={deleting || !cancellationReason.trim()}
          >
            {deleting ? 'Cancelling...' : 'Cancel Service'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Seasonal Contract Confirmation Dialog (for Onboarded Customers) */}
      <Dialog
        open={seasonalContractConfirmDialog.open}
        onClose={() => setSeasonalContractConfirmDialog({ open: false, offer: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Confirm Seasonal Contract Terms</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>Important: Please Review Contract Terms</AlertTitle>
            <Typography variant="body2">
              You are about to accept a <strong>seasonal contract</strong>. Please carefully review the payment schedule and contract terms below before proceeding.
            </Typography>
          </Alert>

          {seasonalContractConfirmDialog.offer && (() => {
            const offer = seasonalContractConfirmDialog.offer;
            const services: Array<{ key: string; name: string; pricing: any }> = [];
            
            if (offer.servicePricing?.lawnMowingTrimming && (offer.servicePricing.lawnMowingTrimming.isSeasonalContract || offer.servicePricing.lawnMowingTrimming.frequency === 'seasonal-contract')) {
              services.push({ key: 'lawnMowingTrimming', name: 'Lawn Mowing/Trimming', pricing: offer.servicePricing.lawnMowingTrimming });
            }
            if (offer.servicePricing?.weedControlFertilizing && (offer.servicePricing.weedControlFertilizing.isSeasonalContract || offer.servicePricing.weedControlFertilizing.frequency === 'seasonal-contract')) {
              services.push({ key: 'weedControlFertilizing', name: 'Weed Control/Fertilizer', pricing: offer.servicePricing.weedControlFertilizing });
            }
            if (offer.servicePricing?.snowRemoval && (offer.servicePricing.snowRemoval.isSeasonalContract || offer.servicePricing.snowRemoval.frequency === 'seasonal-contract')) {
              services.push({ key: 'snowRemoval', name: 'Snow Removal', pricing: offer.servicePricing.snowRemoval });
            }

            return services.map((service, idx) => {
              const seasonalDetails = service.pricing.seasonalContractDetails;
              const paymentSchedule = seasonalDetails?.paymentSchedule || 'single-payment';
              
              // Calculate monthly payment info if monthly (same logic as in the offer display)
              let monthlyPaymentInfo: { monthlyAmount: number; firstPaymentDate: Date; billingMonths: number } | null = null;
              if (paymentSchedule === 'monthly' && seasonalDetails?.totalContractPrice && seasonalDetails?.contractStartDate && seasonalDetails?.contractEndDate) {
                try {
                  const start = new Date(seasonalDetails.contractStartDate);
                  const end = new Date(seasonalDetails.contractEndDate);
                  const startsOnFirst = start.getUTCDate() === 1;
                  
                  let billingMonths: number;
                  if (startsOnFirst) {
                    billingMonths = (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + 
                                 (end.getUTCMonth() - start.getUTCMonth()) + 1;
                  } else {
                    const nextMonthYear = start.getUTCMonth() === 11 ? start.getUTCFullYear() + 1 : start.getUTCFullYear();
                    const nextMonthMonth = (start.getUTCMonth() + 1) % 12;
                    billingMonths = (end.getUTCFullYear() - nextMonthYear) * 12 + 
                                   (end.getUTCMonth() - nextMonthMonth) + 1;
                  }
                  
                  if (billingMonths > 0) {
                    const monthlyAmount = Math.round((seasonalDetails.totalContractPrice / billingMonths) * 100) / 100;
                    let firstPaymentDate: Date;
                    if (startsOnFirst) {
                      firstPaymentDate = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
                    } else {
                      const nextMonthYear = start.getUTCMonth() === 11 ? start.getUTCFullYear() + 1 : start.getUTCFullYear();
                      const nextMonthMonth = (start.getUTCMonth() + 1) % 12;
                      firstPaymentDate = new Date(Date.UTC(nextMonthYear, nextMonthMonth, 1));
                    }
                    monthlyPaymentInfo = { monthlyAmount, firstPaymentDate, billingMonths };
                  }
                } catch (e) {
                  console.error('[SEASONAL-CONFIRM] Error calculating monthly payment:', e);
                }
              }

              return (
                <Box key={idx} sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '2px solid', borderColor: 'primary.main' }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    {service.name}
                  </Typography>
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Total Contract Price:</strong> ${parseFloat(seasonalDetails?.totalContractPrice?.toString() || '0').toFixed(2)}
                    </Typography>
                    
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Service Frequency:</strong> {formatFrequency(seasonalDetails?.serviceFrequency || 'weekly')}
                    </Typography>
                    
                    {seasonalDetails?.contractStartDate && seasonalDetails?.contractEndDate && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Contract Period:</strong> {formatUTCDateString(seasonalDetails.contractStartDate)} - {formatUTCDateString(seasonalDetails.contractEndDate)}
                      </Typography>
                    )}

                    <Box sx={{ mt: 2, p: 2, bgcolor: paymentSchedule === 'monthly' ? 'primary.light' : 'success.light', borderRadius: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                        Payment Schedule: {paymentSchedule === 'monthly' ? 'Monthly Payments' : 'One-Time Payment (Full payment after first service)'}
                      </Typography>
                      
                      {paymentSchedule === 'monthly' && monthlyPaymentInfo ? (
                        <>
                          <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
                            Monthly Amount: <strong>${monthlyPaymentInfo.monthlyAmount.toFixed(2)}</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            First Payment Date: <strong>{formatUTCDateString(monthlyPaymentInfo.firstPaymentDate)}</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Total Payments: <strong>{monthlyPaymentInfo.billingMonths} monthly payments</strong>
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                            Payments will be automatically charged on the <strong>1st of each month</strong> during the contract period.
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Payment Amount: <strong>${parseFloat(seasonalDetails?.totalContractPrice?.toString() || '0').toFixed(2)}</strong>
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                            Full payment will be processed after the <strong>first service is completed</strong>.
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            });
          })()}

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>By proceeding, you agree to:</strong>
            </Typography>
            <Typography variant="body2" component="ul" sx={{ mt: 1, pl: 2 }}>
              <li>The payment schedule shown above</li>
              <li>The contract period and service frequency</li>
              <li>All terms of the seasonal contract</li>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 'medium' }}>
              If the payment schedule is incorrect, please cancel and ask your provider to update the offer with the correct terms.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setSeasonalContractConfirmDialog({ open: false, offer: null })}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              // Close confirmation dialog and proceed to payment setup
              setSeasonalContractConfirmDialog({ open: false, offer: null });
              proceedWithOnboardingPayment(seasonalContractConfirmDialog.offer);
            }}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            I Confirm - Proceed to Setup Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Decline Onboarding Confirmation Dialog */}
      <Dialog
        open={onboardingDeclineDialog.open}
        onClose={handleCancelDeclineOnboarding}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Decline Provider Onboarding</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            You are about to decline your provider's onboarding offer. Please read the following information carefully:
          </DialogContentText>
          
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Important Notice
            </Typography>
            <Typography variant="body2" component="div">
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li>If you decline this offer, the onboarding request will be deleted</li>
                <li>There is <strong>no guarantee</strong> that new marketplace bids will be the same price or lower</li>
                <li>You will <strong>not be able to go back</strong> and accept your provider's original offer after declining</li>
                <li>Future service requests will be subject to a <strong>5% platform fee</strong> on the service price (vs. 1% with onboarding)</li>
              </ul>
            </Typography>
          </Alert>

          {onboardingDeclineDialog.offer && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                Current Provider Offer
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Provider:</strong> {onboardingDeclineDialog.offer.providerName || 'Provider'}
              </Typography>
              {onboardingDeclineDialog.offer.services && (
                <Box sx={{ mt: 1 }}>
                  {onboardingDeclineDialog.offer.services.lawnMowingTrimming && (
                    <Typography variant="body2">
                      • Lawn Mowing/Trimming
                    </Typography>
                  )}
                  {onboardingDeclineDialog.offer.services.weedControlFertilizing && (
                    <Typography variant="body2">
                      • Weed Control/Fertilizer
                    </Typography>
                  )}
                  {onboardingDeclineDialog.offer.services.snowRemoval && (
                    <Typography variant="body2">
                      • Snow Removal
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          <Alert severity="info">
            <Typography variant="body2">
              <strong>Fee Structure:</strong> Accepting your provider&apos;s onboarding maintains a 1% platform fee on the service price.
              Declining means future marketplace requests use a 5% platform fee (first season). A separate payment processing fee is added on top of the service price.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCancelDeclineOnboarding}
            disabled={loading}
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDeclineOnboarding}
            variant="contained"
            color="warning"
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Decline Offer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Method Setup Dialog */}
      <PaymentMethodSetup
        open={paymentSetupDialog.open}
        onClose={handlePaymentSetupClose}
        onSuccess={handlePaymentSetupSuccess}
        bidId={paymentSetupDialog.serviceId}
        customerName={user?.firstName + ' ' + user?.lastName || ''}
        amount={paymentSetupDialog.totalPrice}
      />

      {agreementDialog}
    </Container>
  );
};

export default CustomerDashboard;